import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Button, Menu, MenuItem, Tooltip } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Order, AlertData } from "../Alerts/AlertsUtils/alertsInterfaces";
import {
  getComparator,
  stableSort,
} from "../Alerts/AlertsUtils/alertsTableFunctions";
import { useClipsStore } from "./ClipsStore/clipsStore";
import { EnhancedSMTPTableHead } from "./EnhancedSMTPTableHead";
import {
  formatTimeStampWithTimezone,
  formatLocalTimeToUTC,
} from "./ClipsUtilities/formatTimeStamp";
import { generateNavigationString } from "./ClipsUtilities/generateNavigationString";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { cancelIconRed, cancelIconRedSmall } from "../../common/cancelIconRed";
import {
  checkedCircleIconGrey,
  checkedCircleIconSmall,
} from "../../common/checkedCircleIcon";
import {
  removeClipsIdParamsFromUrl,
} from "./ClipsUtilities/clipsUrlParams";

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

interface SMTPTableProps {
  order: Order;
  orderBy: string;
  setOrder: (order: Order) => void;
  setOrderBy: (orderBy: string) => void;
}

export const SMTPTable = ({
  order,
  orderBy,
  setOrder,
  setOrderBy,
}: SMTPTableProps) => {
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const lastAutoPagedSelectionRef = React.useRef<string | null>(null);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    setPageSMTP,
    pageSMTP,
    smtpData,
    setSmtpData,
    setSelectedRowSMTP,
    setVideoClipLoadingSMTP,
    getVideoURLSMTP,
    smtpSite,
    userFiltersSMTP,
    groupIDSMTP,
    smtpTableFilter,
    setSmtpTableFilter,
  } = useClipsStore();

  // Reset page to 0 when showOnlyClipsWithAlerts changes
  React.useEffect(() => {
    setPageSMTP(0);
  }, [smtpTableFilter, setPageSMTP]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof AlertData
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageSMTP(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageSMTP(0);
  };

  const handleRowClick = (row: any) => {
    // Update all rows to set selected to false
    const updatedData = smtpData.map((item: any) => ({
      ...item,
      selected: false,
    }));

    // Find and update the clicked row to set selected to true
    const updatedDataWithSelection = updatedData.map((item: any) => {
      if (item.clip_id === row.clip_id) {
        return { ...item, selected: true };
      }
      return item;
    });

    // Update the data in the store
    setSmtpData(updatedDataWithSelection);

    // Set the selected row and get video URL
    setVideoClipLoadingSMTP(true);
    setSelectedRowSMTP(row);
    getVideoURLSMTP(row.clip_id, navigate);
    localStorage.setItem("clipsSelectedRow", JSON.stringify(row));
    removeClipsIdParamsFromUrl(setSearchParams, searchParams);
  };

  const filteredData = React.useMemo(
    () =>
      smtpTableFilter === "alerts"
        ? smtpData.filter((row: any) => row.alerts && row.alerts.length > 0)
        : smtpTableFilter === "no_alerts"
        ? smtpData.filter((row: any) => !row.alerts || row.alerts.length === 0)
        : smtpData,
    [smtpData, smtpTableFilter]
  );

  const optionsArrayArray = [
    { name: "All Clips", value: "all" },
    { name: "Only Clips with Alerts", value: "alerts" },
    { name: "Only Clips without Alerts", value: "no_alerts" },
  ];

  const optionMaps = new Map();

  optionsArrayArray.forEach((option: any) => {
    optionMaps.set(`${option.value}`, `${option.name}`);
  });

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const emptyRows =
    pageSMTP > 0
      ? Math.max(0, (1 + pageSMTP) * rowsPerPage - filteredData.length)
      : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(filteredData, getComparator(order, orderBy)).slice(
        pageSMTP * rowsPerPage,
        pageSMTP * rowsPerPage + rowsPerPage
      ),
    [filteredData, order, orderBy, pageSMTP, rowsPerPage]
  );

  React.useEffect(() => {
    const sortedRows = stableSort(filteredData, getComparator(order, orderBy));
    const selectedIndex = sortedRows.findIndex((row: any) => row.selected);
    const selectedClipId: string | null =
      selectedIndex >= 0 && sortedRows[selectedIndex]?.clip_id != null
        ? String(sortedRows[selectedIndex].clip_id)
        : null;
    const expectedPage =
      selectedIndex >= 0 ? Math.floor(selectedIndex / rowsPerPage) : 0;

    if (
      selectedClipId &&
      selectedClipId !== lastAutoPagedSelectionRef.current &&
      expectedPage !== pageSMTP
    ) {
      lastAutoPagedSelectionRef.current = selectedClipId;
      setPageSMTP(expectedPage);
    }
  }, [filteredData, order, orderBy, pageSMTP, rowsPerPage, setPageSMTP]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", paddingBottom: "10px" }}>
      <TableContainer sx={{ maxHeight: 550 }}>
        <div className="pl-[10px] pt-[5px] h-[30px] flex">
          <p className="text-[14px] pt-[7px]">FILTER BY</p>
          <div className="pl-[2px]">
            <Button
              id="filter-button"
              aria-controls={open ? "filter-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
            >
              {optionMaps.get(smtpTableFilter)}
            </Button>
            <Menu
              id="filter-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "filter-button",
              }}
            >
              {optionsArrayArray.map((option: any) => {
                return (
                  <MenuItem
                    key={option.value}
                    onClick={() => {
                      setSmtpTableFilter(option.value);
                      const filteredData =
                        option.value === "alerts"
                          ? smtpData.filter(
                              (row: any) => row.alerts && row.alerts.length > 0
                            )
                          : option.value === "no_alerts"
                          ? smtpData.filter(
                              (row: any) =>
                                !row.alerts || row.alerts.length === 0
                            )
                          : smtpData;
                      handleRowClick(filteredData[0]);
                      handleClose();
                    }}
                  >
                    {option.name}
                  </MenuItem>
                );
              })}
            </Menu>
          </div>
          <div className="pl-[1px]">
            <Tooltip
              title={`Note: In certain cases, multiple clips may be associated to the same alert. This may explain a difference in count between the number of "Only Clips with Alerts" and the number of alerts on the Alerts/Analytics pages of our UI."`}
              placement="top"
              sx={{ fontSize: "10px", fontFamily: "mulish" }}
            >
              <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
            </Tooltip>
          </div>
        </div>
        <Table
          sx={{ fontFamily: "Mulish", postion: "absolute" }}
          aria-labelledby="clips-table"
          stickyHeader
        >
          <EnhancedSMTPTableHead
            onRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
          />
          <TableBody>
            {visibleRows.map((row: any) => {
              let sx: any = { cursor: "pointer" };
              if (row.selected) {
                sx = {
                  cursor: "pointer",
                  backgroundColor: "rgba(2, 136, 209, 0.2)",
                };
              }
              return (
                <TableRow
                  hover
                  onClick={() => handleRowClick(row)}
                  tabIndex={-1}
                  key={row.id}
                  sx={sx}
                >
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "20%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {smtpSite.site_name}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "20%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {row.camera_name}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "20%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {row.local_time_received
                      ? formatLocalTimeToUTC(row.local_time_received)
                      : ""}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "20%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {row.local_time_received
                      ? formatTimeStampWithTimezone(row.local_time_received)
                      : ""}
                  </TableCell>
                  {/* <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.clip_result}
                  </TableCell> */}
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "20%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {row.clip_id}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "10%",
                    }}
                  >
                    {row.armed ? checkedCircleIconSmall : cancelIconRedSmall}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="absolute pt-[12px] pl-[5px] z-20 flex">
        {/* <ThemeProvider theme={theme}>
          <Button
            type="button"
            color="primary"
            size="small"
            onClick={() => { }}
            disabled={false}
            className="hover:bg-[#E67300]"
          >
            Download Report
          </Button>
        </ThemeProvider> */}
        <div className="pl-[8px] flex">
          <ThemeProvider theme={theme}>
            <Button
              type="button"
              color="primary"
              size="small"
              onClick={() => {
                const navigationString = generateNavigationString(
                  userFiltersSMTP,
                  groupIDSMTP,
                  "alerts"
                );
                window.open(`/alerts?${navigationString}`, "_blank");
              }}
              disabled={false}
              className="hover:bg-[#E67300]"
            >
              See Alerts
            </Button>
          </ThemeProvider>
          <div className="pl-[8px]">
            <ThemeProvider theme={theme}>
              <Button
                type="button"
                color="primary"
                size="small"
                onClick={() => {
                  const navigationString = generateNavigationString(
                    userFiltersSMTP,
                    groupIDSMTP,
                    "analytics"
                  );
                  window.open(`/analytics?${navigationString}`, "_blank");
                }}
                disabled={false}
                className="hover:bg-[#E67300]"
              >
                See Analytics
              </Button>
            </ThemeProvider>
          </div>
        </div>
      </div>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={pageSMTP}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
